import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdTreadAgentConversationsId } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';

import { TreadAgentConversation } from '~hooks/useTreadAgent/models';
import { treadAgentConversationQueryKey } from '~queries/treadAgentQueryKeys';
import connection from '~services/connectionModule';
import { Nullable } from '~types/Nullable';

/**
 * A single conversation's persisted history (messages + still-pending tool calls).
 * The active turn's streamed parts are overlaid by the view from TreadAgentStore.
 */
export default function useTreadAgentConversationQuery(
  companyId: Nullable<string>,
  conversationId: Nullable<string>,
) {
  const query = useQuery({
    queryKey: treadAgentConversationQueryKey(companyId, conversationId),
    queryFn:
      companyId && conversationId
        ? async ({ signal }) => {
            try {
              const response = await getV1CompaniesCompanyIdTreadAgentConversationsId({
                path: { 'company-id': companyId, id: conversationId },
                signal,
              });
              return TreadAgentConversation.parse(response.data.data);
            } catch (error) {
              // A 404 means the conversation isn't in the current company scope (e.g. right
              // after switching company) or no longer exists — degrade to null quietly
              // rather than a scary error toast; the page falls back to the landing.
              if (isAxiosError(error) && error.response?.status === 404) {
                return null;
              }

              connection.handleRequestError(
                error,
                t('error_messages.tread_agent.failed_to_fetch_chat'),
              );
              throw error;
            }
          }
        : skipToken,
  });

  return { conversation: query.data ?? null, isFetching: query.isFetching };
}
